import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Card, DataState, PageHeader } from '@/components/Common'
import { CheckField, Field, FormSection, TextArea, TextField } from '@/components/Form'
import { ConfirmDialog, Modal } from '@/components/Modal'
import { FilterEditor } from '@/pages/Filters'
import { useAuth } from '@/lib/auth'
import { useFetch } from '@/lib/hooks'
import { useResource } from '@/lib/resource'
import type { FacilityTree, Listener, Reference, Rule, Worklist } from '@/lib/api'

const EMPTY = {
  name: '',
  description: '',
  priority: 100,
  calling_ae_pattern: '',
  called_ae_pattern: '',
  source_ip_pattern: '',
  listener_port: null as number | null,
  match_facility_id: null as number | null,
  match_department_id: null as number | null,
  facility_scope: 'inherit' as 'inherit' | 'device',
  department_scope: 'inherit' as 'inherit' | 'device',
  worklist_id: 0,
  stop_on_match: true,
  enabled: true,
  notes: '',
}

type Draft = typeof EMPTY

/**
 * Which caller sees which worklist.
 *
 * The page is a table in EVALUATION ORDER, because the order is the behaviour:
 * a rule with stop-on-match ends evaluation, so what sits above it decides
 * whether it is ever reached. Sorting this list any other way would be lying
 * about what happens.
 */
export function Rules() {
  const rules = useResource<Rule>('/api/v1/rules')
  const worklists = useFetch<Worklist[]>('/api/v1/worklists')
  const listeners = useFetch<Listener[]>('/api/v1/listeners')
  const tenancy = useFetch<FacilityTree[]>('/api/v1/tenancy')
  const reference = useFetch<Reference>('/api/v1/system/reference')
  const { can } = useAuth()

  const [editing, setEditing] = useState<Rule | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Rule | null>(null)
  const [filtersFor, setFiltersFor] = useState<Rule | null>(null)

  const mayEdit = can('worklist_admin')
  const list = rules.data ?? []

  const move = async (index: number, delta: number) => {
    const next = [...list]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    await rules.action('/api/v1/rules/reorder', { rule_ids: next.map((r) => r.id) })
  }

  return (
    <>
      <PageHeader
        title="Rules"
        subtitle="Evaluated top to bottom. The first match usually wins."
        actions={
          mayEdit && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              Add rule
            </button>
          )
        }
      />

      {rules.saveError && <div className="notice notice-error">{rules.saveError}</div>}

      <Card>
        <p className="card-note">
          A caller matching <strong>no rule</strong> receives an empty worklist
          and a Success status — which is correct, because its permission was
          already settled at the listener. That is indistinguishable at the
          modality from having no work, so the{' '}
          <Link to="/query-log">query log</Link> and the{' '}
          <Link to="/simulator">simulator</Link> are how you find out a rule is
          missing.
        </p>

        <DataState
          loading={rules.loading}
          error={rules.error}
          empty={list.length === 0}
          emptyMessage="No rules. Every caller currently receives an empty worklist."
        />

        {list.length > 0 && (
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th style={{ width: '5rem' }}>Order</th>
                  <th>Rule</th>
                  <th>Matches a caller with…</th>
                  <th>Serves</th>
                  <th>Scope</th>
                  <th>Then</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((rule, index) => (
                  <tr key={rule.id} className={rule.enabled ? undefined : 'row-muted'}>
                    <td className="num">
                      {mayEdit ? (
                        <span className="actions">
                          <button
                            className="btn btn-small"
                            disabled={index === 0 || rules.saving}
                            onClick={() => void move(index, -1)}
                            title="Evaluate earlier"
                          >
                            ↑
                          </button>
                          <button
                            className="btn btn-small"
                            disabled={index === list.length - 1 || rules.saving}
                            onClick={() => void move(index, 1)}
                            title="Evaluate later"
                          >
                            ↓
                          </button>
                        </span>
                      ) : (
                        rule.priority
                      )}
                    </td>

                    <td>
                      <strong>{rule.name}</strong>
                      {!rule.enabled && <> <Badge state="DISABLED" /></>}
                      {rule.description && <div className="hint">{rule.description}</div>}
                      {rule.filters.length > 0 && (
                        <div className="hint">
                          {rule.filters.length} filter
                          {rule.filters.length === 1 ? '' : 's'}
                          {rule.filters.some((f) => !f.resolvable) && (
                            <span className="warn-text"> — one cannot be resolved</span>
                          )}
                        </div>
                      )}
                    </td>

                    <td className="mono">
                      <Clauses rule={rule} />
                    </td>

                    <td>
                      {rule.worklist_name}
                      {!rule.worklist_enabled && (
                        <div className="warn-text">worklist disabled — serves nothing</div>
                      )}
                    </td>

                    <td>
                      <ScopeCell rule={rule} />
                    </td>

                    <td>
                      {rule.stop_on_match ? (
                        <span className="hint">stop</span>
                      ) : (
                        <span className="tag">continue</span>
                      )}
                    </td>

                    <td className="actions">
                      <button className="btn btn-small" onClick={() => setFiltersFor(rule)}>
                        Filters
                      </button>
                      {mayEdit && (
                        <>
                          <button className="btn btn-small" onClick={() => setEditing(rule)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-small btn-danger-quiet"
                            onClick={() => setDeleting(rule)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {list.some((r) => !r.stop_on_match) && (
          <p className="table-footnote">
            A rule marked <strong>continue</strong> does not end evaluation, so
            the caller can be served more than one worklist in a single
            response. That is how a combined room gets two lists.
          </p>
        )}
      </Card>

      {editing && (
        <RuleEditor
          rule={editing === 'new' ? null : editing}
          worklists={worklists.data ?? []}
          listeners={listeners.data ?? []}
          tenancy={tenancy.data ?? []}
          reference={reference.data ?? null}
          saving={rules.saving}
          error={rules.saveError}
          onClose={() => {
            setEditing(null)
            rules.clearError()
          }}
          onSave={async (draft) => {
            const saved =
              editing === 'new'
                ? await rules.create(draft)
                : await rules.update(editing.id, draft)
            if (saved) setEditing(null)
          }}
        />
      )}

      {filtersFor && (
        <FilterEditor
          ownerKind="rule"
          ownerId={filtersFor.id}
          ownerName={filtersFor.name}
          reference={reference.data ?? null}
          onClose={() => {
            setFiltersFor(null)
            rules.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete rule '${deleting.name}'?`}
          confirmLabel="Delete rule"
          destructive
          busy={rules.saving}
          error={rules.saveError}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            if (await rules.remove(deleting.id)) setDeleting(null)
          }}
        >
          <p>
            Any caller this rule was catching will fall through to the rules
            below it, or to no rule at all — in which case it receives an empty
            worklist with no error.
          </p>
          <p className="hint">
            Disabling it instead keeps the row and its notes, and is reversible
            in one click.
          </p>
        </ConfirmDialog>
      )}
    </>
  )
}

function Clauses({ rule }: { rule: Rule }) {
  const clauses: string[] = []
  if (rule.calling_ae_pattern) clauses.push(`AE ${rule.calling_ae_pattern}`)
  if (rule.called_ae_pattern) clauses.push(`called ${rule.called_ae_pattern}`)
  if (rule.source_ip_pattern) clauses.push(`from ${rule.source_ip_pattern}`)
  if (rule.listener_port) clauses.push(`port ${rule.listener_port}`)
  if (rule.match_facility_code) clauses.push(`at ${rule.match_facility_code}`)
  if (rule.match_department_code) clauses.push(`serving ${rule.match_department_code}`)

  if (clauses.length === 0) {
    return <span className="tag">any caller</span>
  }
  return <>{clauses.join(' · ')}</>
}

function ScopeCell({ rule }: { rule: Rule }) {
  const parts: string[] = []
  if (rule.facility_scope === 'device') parts.push("device's facility")
  if (rule.department_scope === 'device') parts.push("device's departments")
  if (parts.length === 0) return <span className="hint">worklist's own</span>
  return <span className="tag">{parts.join(' + ')}</span>
}

function RuleEditor({
  rule,
  worklists,
  listeners,
  tenancy,
  reference,
  saving,
  error,
  onClose,
  onSave,
}: {
  rule: Rule | null
  worklists: Worklist[]
  listeners: Listener[]
  tenancy: FacilityTree[]
  reference: Reference | null
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Draft) => void
}) {
  const [draft, setDraft] = useState<Draft>(() =>
    rule
      ? {
          name: rule.name,
          description: rule.description,
          priority: rule.priority,
          calling_ae_pattern: rule.calling_ae_pattern,
          called_ae_pattern: rule.called_ae_pattern,
          source_ip_pattern: rule.source_ip_pattern,
          listener_port: rule.listener_port,
          match_facility_id: rule.match_facility_id,
          match_department_id: rule.match_department_id,
          facility_scope: rule.facility_scope,
          department_scope: rule.department_scope,
          worklist_id: rule.worklist_id,
          stop_on_match: rule.stop_on_match,
          enabled: rule.enabled,
          notes: rule.notes,
        }
      : { ...EMPTY, worklist_id: worklists[0]?.id ?? 0 },
  )

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const departments = tenancy.flatMap((f) =>
    f.departments.map((d) => ({ ...d, facility_code: f.code })),
  )

  const noClauses =
    !draft.calling_ae_pattern &&
    !draft.called_ae_pattern &&
    !draft.source_ip_pattern &&
    !draft.listener_port &&
    !draft.match_facility_id &&
    !draft.match_department_id

  return (
    <Modal
      title={rule ? `Edit rule '${rule.name}'` : 'Add rule'}
      subtitle="Matching decides whether the rule fires. Scope decides what it then serves."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving || !draft.name || !draft.worklist_id}
            onClick={() => onSave(draft)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}

      <FormSection title="Identity">
        <div className="form-grid">
          <TextField label="Name" value={draft.name} onChange={(v) => set('name', v)} required />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(v) => set('description', v)}
          />
        </div>
        <CheckField
          label="Enabled"
          detail="A disabled rule is skipped entirely."
          checked={draft.enabled}
          onChange={(v) => set('enabled', v)}
        />
      </FormSection>

      <FormSection
        title="Matching — does this rule fire?"
        tone="inbound"
        description={
          <>
            Every clause left empty matches anything. All of them are ANDed, so
            adding one can only narrow which callers this rule catches.
            {noClauses && (
              <div className="notice notice-warn">
                With no clauses set, this rule matches <strong>every</strong>{' '}
                caller. That is a legitimate catch-all, but put it last.
              </div>
            )}
          </>
        }
      >
        <div className="form-grid">
          <TextField
            label="Calling AE title"
            value={draft.calling_ae_pattern}
            onChange={(v) => set('calling_ae_pattern', v)}
            maxLength={64}
            placeholder="CT_*"
            hint="Exact, or a glob: * for any run, ? for one character. Case-insensitive."
          />
          <TextField
            label="Called AE title"
            value={draft.called_ae_pattern}
            onChange={(v) => set('called_ae_pattern', v)}
            maxLength={64}
            hint="What the device addressed us by. Never causes a refusal; only matching."
          />
          <TextField
            label="Source address"
            value={draft.source_ip_pattern}
            onChange={(v) => set('source_ip_pattern', v)}
            maxLength={200}
            placeholder="10.1.230.0/24"
            hint="Exact, CIDR, a glob, or a comma-separated list of those. CIDR is usually what you want — a VLAN is the natural unit of 'the modalities in radiology'."
          />
          <Field
            label="Listener"
            hint="Limit this rule to callers arriving on one port."
          >
            <select
              value={draft.listener_port ?? ''}
              onChange={(e) =>
                set('listener_port', e.target.value === '' ? null : Number(e.target.value))
              }
            >
              <option value="">any port</option>
              {listeners.map((l) => (
                <option key={l.id} value={l.listen_port}>
                  {l.name} — {l.listen_port}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Device is at facility"
            hint="The rule fires only for a device registered to this site. A device with no facility never matches."
          >
            <select
              value={draft.match_facility_id ?? ''}
              onChange={(e) =>
                set('match_facility_id', e.target.value === '' ? null : Number(e.target.value))
              }
            >
              <option value="">any facility</option>
              {tenancy.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.code} — {f.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Device serves department"
            hint="Membership, not equality: a device serving three departments matches a rule naming any one of them."
          >
            <select
              value={draft.match_department_id ?? ''}
              onChange={(e) =>
                set(
                  'match_department_id',
                  e.target.value === '' ? null : Number(e.target.value),
                )
              }
            >
              <option value="">any department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.facility_code} / {d.code} — {d.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </FormSection>

      <FormSection title="What it serves" tone="outbound">
        <Field label="Worklist" required>
          <select
            value={draft.worklist_id}
            onChange={(e) => set('worklist_id', Number(e.target.value))}
          >
            {worklists.length === 0 && <option value={0}>no worklists defined</option>}
            {worklists.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.enabled ? '' : ' (disabled)'}
              </option>
            ))}
          </select>
        </Field>

        <CheckField
          label="Stop evaluating after this rule"
          detail={
            draft.stop_on_match
              ? 'The usual setting. Rules below this one are not considered for a caller it matches.'
              : 'Evaluation continues, so this caller can be served several worklists in one response. Use for a combined room.'
          }
          checked={draft.stop_on_match}
          onChange={(v) => set('stop_on_match', v)}
        />
      </FormSection>

      <FormSection
        title="Scope — narrowing what it serves"
        description={
          <>
            <strong>Fails closed.</strong> If a scope is set to the calling
            device's own facility or departments and the device has none, this
            rule matches and then returns <em>nothing</em>, loudly. That is
            deliberate: a too-wide list across hospitals is a privacy incident
            and looks exactly like a busy day.
          </>
        }
      >
        <div className="form-grid">
          <Field
            label="Facility"
            hint={reference?.scopes?.device ?? ''}
          >
            <select
              value={draft.facility_scope}
              onChange={(e) => set('facility_scope', e.target.value as 'inherit' | 'device')}
            >
              <option value="inherit">use the worklist's own scope</option>
              <option value="device">narrow to the calling device's facility</option>
            </select>
          </Field>
          <Field label="Departments">
            <select
              value={draft.department_scope}
              onChange={(e) => set('department_scope', e.target.value as 'inherit' | 'device')}
            >
              <option value="inherit">use the worklist's own scope</option>
              <option value="device">narrow to the departments the device serves</option>
            </select>
          </Field>
        </div>
        {draft.facility_scope === 'device' && (
          <div className="notice notice-info">
            This is the pattern that makes multiple sites tractable: one shared
            worklist, and each hospital's devices see their own slice of it.
            Opening another site becomes a facility row and some device rows —
            no new worklist and no new rule.
          </div>
        )}
      </FormSection>

      <FormSection title="Notes">
        <TextArea
          label="Why this rule exists"
          value={draft.notes}
          onChange={(v) => set('notes', v)}
          maxLength={1000}
          hint="Worth writing. Rule order is easy to read and hard to explain eighteen months later."
        />
      </FormSection>
    </Modal>
  )
}
